import type {
  AnchorProfile,
  BrainCharacter,
  IEntityService,
} from "@brains/plugins";
import type { Logger } from "@brains/utils/logger";
import {
  siteInfoSchema,
  type ResolvedSiteInfoBody,
  type SiteInfoBody,
} from "../schemas/site-info-schema";
import { SiteInfoAdapter } from "../adapters/site-info-adapter";

/**
 * Site Info Service
 * Provides the site's information (title, description, CTA, etc.)
 */
interface SiteInfoIdentitySource {
  getBrain: () => BrainCharacter;
  getAnchor: () => AnchorProfile;
}

const defaultIdentitySource: SiteInfoIdentitySource = {
  getBrain: () => ({
    name: "Brain",
    role: "Knowledge assistant",
    purpose: "A knowledge management system",
    values: [],
  }),
  getAnchor: () => ({
    name: "Brain",
    description: "A knowledge management system",
  }),
};

export class SiteInfoService {
  private logger: Logger;
  private entityService: IEntityService;
  private adapter: SiteInfoAdapter;
  private defaultSiteInfo: SiteInfoBody;
  private identitySource: SiteInfoIdentitySource;

  /**
   * Get the default site info for a new site
   */
  public static getDefaultSiteInfo(): ResolvedSiteInfoBody {
    return {
      represents: "anchor",
      title: "Brain",
      description: "A knowledge management system",
    };
  }

  /**
   * Create a fresh instance without affecting singleton
   */
  public static createFresh(
    entityService: IEntityService,
    logger: Logger,
    defaultSiteInfo?: Partial<SiteInfoBody>,
    identitySource?: SiteInfoIdentitySource,
  ): SiteInfoService {
    return new SiteInfoService(
      entityService,
      logger,
      defaultSiteInfo,
      identitySource,
    );
  }

  /**
   * Private constructor to enforce factory methods
   */
  private constructor(
    entityService: IEntityService,
    logger: Logger,
    defaultSiteInfo?: Partial<SiteInfoBody>,
    identitySource: SiteInfoIdentitySource = defaultIdentitySource,
  ) {
    this.entityService = entityService;
    this.logger = logger.child("SiteInfoService");
    this.adapter = new SiteInfoAdapter();
    this.identitySource = identitySource;

    this.defaultSiteInfo = {
      represents: "anchor",
      ...defaultSiteInfo,
    };
  }

  /**
   * Initialize the service
   * Creates a default site info if none exists
   */
  public async initialize(): Promise<void> {
    try {
      const siteInfo = await this.entityService.getEntity({
        entityType: "site-info",
        id: "site-info",
      });

      // If no site info exists, create one with default values. Re-check
      // immediately before creating defaults because startup directory sync may
      // import the singleton after the first DB miss on cold boot.
      if (!siteInfo) {
        const recheckedSiteInfo = await this.entityService.getEntity({
          entityType: "site-info",
          id: "site-info",
        });
        if (recheckedSiteInfo) return;

        this.logger.info("No site info found, creating default site info");
        const content = this.adapter.createSiteInfoContent(
          this.defaultSiteInfo,
        );

        await this.entityService.createEntity({
          entity: {
            id: "site-info",
            entityType: "site-info",
            content,
            metadata: {},
          },
        });

        this.logger.info("Default site info created successfully");
      }
    } catch (error) {
      this.logger.error("Failed to initialize site info", { error });
    }
  }

  /**
   * Get the site info data (from database or default)
   * Always loads fresh from database to ensure consistency
   */
  public async getSiteInfo(): Promise<ResolvedSiteInfoBody> {
    try {
      // Always load fresh from database to avoid stale cache issues
      const siteInfo = await this.entityService.getEntity(
        {
          entityType: "site-info",
          id: "site-info",
        },
        siteInfoSchema,
      );

      if (siteInfo) {
        return this.resolveIdentityFallbacks(
          this.adapter.parseSiteInfoBody(siteInfo.content),
        );
      }
    } catch (error) {
      this.logger.debug("Site info not found, using defaults", { error });
    }
    return this.resolveIdentityFallbacks(this.defaultSiteInfo);
  }

  private resolveIdentityFallbacks(
    siteInfo: SiteInfoBody,
  ): ResolvedSiteInfoBody {
    if (siteInfo.represents === "brain") {
      const brain = this.identitySource.getBrain();
      return {
        ...siteInfo,
        title: siteInfo.title ?? brain.name,
        description: siteInfo.description ?? brain.purpose,
      };
    }

    const anchor = this.identitySource.getAnchor();
    return {
      ...siteInfo,
      title: siteInfo.title ?? anchor.name,
      description:
        siteInfo.description ??
        anchor.description ??
        `The public site for ${anchor.name}`,
    };
  }
}
